import * as React from "react"
import { useNavigate, useParams } from "react-router"

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useToast, type ToastTone } from "@/components/ui/toast"
import { useCurrentUser } from "@/features/auth"
import { bannerFor } from "@/lib/api-error-message"
import { canConverse } from "@/lib/roles"

import { useCreateReview } from "@/features/intake/api/create-review"

import { canReReadPage } from "./blocker-state"
import { ReviewBlockerStrip } from "./review-blocker-strip"
import { QueryErrorState, ReviewGoneState, WorkspaceSkeleton } from "./review-page-states"

import {
  AddFindingDialog,
  type AddFindingInput,
  type DrawContext,
} from "./findings/add-finding-dialog"
import {
  useAddFindingMutation,
  useAdvanceStageMutation,
  useArchiveReviewMutation,
  useUnarchiveReviewMutation,
  useAssignReviewMutation,
  useEditNarrativeMutation,
  useLiveConversation,
  useTypingSignal,
  useLiveInvalidation,
  useMarkCorrectedMutation,
  useAnswerUnreadPagesMutation,
  useForceOcrMutation,
  useMarkMessagesReadMutation,
  useRunNowMutation,
  useReviewVersionsQuery,
  type UnreadPagesDecision,
  usePostMessageMutation,
  useReturnToPoolMutation,
  useReviewLive,
  useReviewQuery,
  useSetVerdictMutation,
  useSkipSecondPassMutation,
} from "./api"
import { useReviewCommentsQuery } from "./api/use-review-comments"
import { useReviewHistoryQuery } from "./api/use-review-history"
import { useReviewPeopleQuery } from "./api/use-review-people"
import { FindingsRail } from "./findings/findings-rail"
import { MessagesSheet } from "./messages/messages-sheet"
import { PdfControls, PdfZoomControl } from "./pdf/pdf-controls"
import { FindingRibbon } from "./pdf/finding-ribbon"
import { PdfSurface } from "./pdf/pdf-surface"
import { PeopleRoutingSheet } from "./people/people-routing-sheet"
import { ReviewHeader, type ReviewViewMode } from "./review-header"
import { ReviewShortcuts } from "./review-shortcuts"
import {
  isRunning,
  type AssignmentEvent,
  type FindingId,
  type Message,
  type Review,
  type ReviewId,
  type Role,
  type UserId,
} from "./data/review-data"
import { PAGE_SECTIONS, RULE_CATALOG } from "./data/rule-catalog"
import { buildCompareOptions, MASTER_COMPARE_ID } from "./pdf/compare-options"
import { SideBySide } from "./pdf/side-by-side"
import { SplitPane } from "./pdf/split-pane"
import { useReviewHotkeys } from "./use-review-hotkeys"

/**
 * Review workspace route component.
 *
 * This file is composition, not logic. It owns the local UI state that
 * spans the rail and the document surface (active finding, expanded and
 * editing pointers, view mode, page and zoom) and dispatches user
 * intents to the API layer. Every feature-level concern — data
 * fetching, mutations, realtime, keyboard, rendering — lives in a
 * sibling module.
 *
 * Data flow: `useReviewQuery` yields the review; mutation hooks apply
 * optimistic updates via the shared cache and roll back on failure.
 * Local UI state is intentionally not persisted — a fresh mount is
 * always a fresh session.
 */
export function ReviewPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const reviewId = (id ?? null) as ReviewId | null
  const query = useReviewQuery(reviewId)

  useLiveInvalidation(reviewId, query.refetch)
  // The detail query carries no messages (adapter sets `messages: []`), so the
  // conversation needs its own live refresh rather than riding on that refetch.
  useLiveConversation(reviewId)

  if (query.isSuccess && query.data === null) return <ReviewGoneState />

  if (query.isError) {
    return <QueryErrorState error={query.error} onRetry={query.refetch} />
  }

  if (query.isPending || !query.data) return <WorkspaceSkeleton />

  return <Workspace key={query.data.id} review={query.data} />
}

const EMPTY_COLLEAGUES: ReadonlyArray<{ userId: UserId; name: string; role: Role }> = []
const EMPTY_MESSAGES: readonly Message[] = []
const EMPTY_HISTORY: readonly AssignmentEvent[] = []

function Workspace({ review }: { review: Review }): JSX.Element {
  const reviewId = review.id
  const { toast } = useToast()

  const live = useReviewLive(reviewId)
  const signalTyping = useTypingSignal(reviewId)

  const { data: people } = useReviewPeopleQuery(reviewId)

  // Most reviews have one version, so this collapses to the master alone and
  // the selector hides itself.
  const { data: versions } = useReviewVersionsQuery(reviewId)
  const compareOptions = React.useMemo(
    () =>
      buildCompareOptions(
        {
          url: review.masterUrl,
          name: review.masterName,
          regNo: review.epaRegNo,
          pageCount: review.pageCount,
        },
        { name: review.fplName },
        versions ?? [],
        reviewId,
      ),
    [
      review.masterUrl,
      review.masterName,
      review.epaRegNo,
      review.pageCount,
      review.fplName,
      versions,
      reviewId,
    ],
  )
  const colleagues = people?.assignable ?? EMPTY_COLLEAGUES

  const { data: conversation } = useReviewCommentsQuery(reviewId)
  const messages = conversation?.messages ?? EMPTY_MESSAGES
  const totalUnread = conversation?.totalUnread ?? 0

  const resolveName = React.useCallback(
    (userId: number | null): string | null => {
      if (userId == null) return null
      const key = String(userId)
      const pool = [
        people?.assignee,
        people?.owner,
        ...(people?.clientContacts ?? []),
        ...(people?.assignable ?? []),
      ]
      for (const c of pool) {
        if (c && c.userId === key) return c.name
      }
      return null
    },
    [people],
  )
  const { data: history } = useReviewHistoryQuery(reviewId, resolveName)
  const events = history ?? EMPTY_HISTORY

  const setVerdict = useSetVerdictMutation()
  const markCorrected = useMarkCorrectedMutation()
  const editNarrative = useEditNarrativeMutation()
  const addFinding = useAddFindingMutation()
  const assignReview = useAssignReviewMutation()
  const returnToPool = useReturnToPoolMutation()
  const advanceStage = useAdvanceStageMutation()
  const skipSecondPass = useSkipSecondPassMutation()
  const archiveReview = useArchiveReviewMutation()
  const unarchiveReview = useUnarchiveReviewMutation()
  const postMessage = usePostMessageMutation()
  const markMessagesRead = useMarkMessagesReadMutation()
  const answerUnreadPages = useAnswerUnreadPagesMutation()
  const runNow = useRunNowMutation()
  const resubmit = useCreateReview()
  const forceOcr = useForceOcrMutation()

  const navigate = useNavigate()

  /*
     Which answer is in flight, so one button spins while the rest only
     disable. Without it every button would spin and the reviewer could not
     tell which decision they had actually made.
  */
  const [answerInFlight, setAnswerInFlight] = React.useState<UnreadPagesDecision | null>(null)

  /* Any mutation error is surfaced as a single top-of-page banner so
     the user does not need to hunt for which action failed. */
  const globalError =
    /* The re-read's refusals are mostly environmental — no OCR reader
       configured — so they are not knowable when the control is drawn and have
       to be said after it is pressed. */
    forceOcr.error ??
    setVerdict.error ??
    markCorrected.error ??
    editNarrative.error ??
    addFinding.error ??
    assignReview.error ??
    returnToPool.error ??
    advanceStage.error ??
    skipSecondPass.error ??
    archiveReview.error ??
    unarchiveReview.error ??
    postMessage.error ??
    null

  // Through the shared classifier, not `error.message`: a raw message is a
  // backend sentence at best and "[object Object]" at worst. `"save"` because
  // every mutation feeding this banner is a write, and a reviewer's first
  // question is whether their change survived.
  const globalErrorBanner = bannerFor(globalError, "this review", "save")

  const dismissGlobalError = React.useCallback(() => {
    setVerdict.reset()
    markCorrected.reset()
    editNarrative.reset()
    addFinding.reset()
    assignReview.reset()
    returnToPool.reset()
    advanceStage.reset()
    skipSecondPass.reset()
    archiveReview.reset()
    forceOcr.reset()
    unarchiveReview.reset()
    postMessage.reset()
  }, [
    setVerdict,
    markCorrected,
    editNarrative,
    addFinding,
    assignReview,
    returnToPool,
    advanceStage,
    skipSecondPass,
    archiveReview,
    unarchiveReview,
    forceOcr,
    postMessage,
  ])

  const [view, setView] = React.useState<ReviewViewMode>("findings")
  const [activeId, setActiveId] = React.useState<FindingId | null>(null)
  const [expandedId, setExpandedId] = React.useState<FindingId | null>(null)
  const [editingId, setEditingId] = React.useState<FindingId | null>(null)
  /* Who is acting, for the routing guards — null while the session resolves. */
  const currentUser = useCurrentUser()

  /*
     The server refuses a re-read on a review that is running, on one parked on
     its own unread-pages question, and for a principal who may not spend. All
     three are known here, so the control is withheld rather than offered and
     then refused — and the fourth refusal, an unconfigured OCR lane, is not
     knowable from the client, which is why the error is surfaced where it is
     pressed.
  */
  const reReadAllowed = canReReadPage(review, currentUser?.role)
  // Held here, not in the pane, so it survives a switch to the findings view and back.
  const [compareId, setCompareId] = React.useState(MASTER_COMPARE_ID)
  // Real length from pdf.js; the ribbon needs a scale to place marks on.
  const [fplPages, setFplPages] = React.useState(1)
  const [peopleOpen, setPeopleOpen] = React.useState(false)
  const [messagesOpen, setMessagesOpen] = React.useState(false)
  const [addFindingOpen, setAddFindingOpen] = React.useState(false)
  const [drawMode, setDrawMode] = React.useState(false)
  const [drawContext, setDrawContext] = React.useState<DrawContext | null>(null)
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false)
  const [page, setPage] = React.useState(1)
  const [zoom, setZoom] = React.useState(1)
  // Focus request: nonce-bumped so re-focusing the same finding still fires.
  // PdfSurface owns the two-phase (setZoom → wait for render → scroll) so the
  // scroll never runs against a layout that hasn't landed yet.
  const [focusReq, setFocusReq] = React.useState<{ findingId: FindingId; nonce: number } | null>(
    null,
  )
  // The zoom we were at before the last focus, so the pill can revert to it.
  const [preFocusZoom, setPreFocusZoom] = React.useState<number | null>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  /**
   * Findings in rail order, carrying their conversation counts.
   *
   * The review payload has no per-finding comment count, so it is derived from
   * the conversation this screen already loads rather than left at zero — a
   * finding with discussion on it has to look different from one without.
   */
  const orderedFindings = React.useMemo(() => {
    const counts = new Map<FindingId, { total: number; unread: number }>()
    for (const message of conversation?.messages ?? []) {
      if (message.findingId == null) continue
      const tally = counts.get(message.findingId) ?? { total: 0, unread: 0 }
      tally.total += 1
      if (message.unread) tally.unread += 1
      counts.set(message.findingId, tally)
    }

    return [...review.findings]
      .map((f) => {
        const tally = counts.get(f.id)
        return tally ? { ...f, commentCount: tally.total, unreadCount: tally.unread } : f
      })
      .sort((a, b) => {
        if (a.group.ordinal !== b.group.ordinal) return a.group.ordinal - b.group.ordinal
        return a.number - b.number
      })
  }, [review.findings, conversation])

  // A view preference, not review state: it is not persisted and not shared,
  // because it says what this reader wants to look at right now.
  const [showBoxes, setShowBoxes] = React.useState(true)

  const focusFinding = React.useCallback(
    (id: FindingId) => {
      setActiveId(id)
      setExpandedId(id)
      setEditingId(null)
      const f = orderedFindings.find((x) => x.id === id)
      if (f?.bbox) setPage(f.bbox.page)
      // Selection alone doesn't zoom — the reviewer opts in via the pill.
      // Changing selection while framed unframes: the pill's revert-zoom
      // would otherwise send them to a zoom they no longer have in mind.
      setPreFocusZoom(null)
      // Scroll the rail row into view. The PDF box is scrolled into view by
      // PdfSurface itself, which knows when its own layout has settled.
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-finding-id="${CSS.escape(id)}"]`)
        el?.scrollIntoView({ behavior: "smooth", block: "center" })
      })
    },
    [orderedFindings],
  )

  /**
   * Select a finding and frame it, or unframe if it is already framed.
   *
   * A toggle rather than a one-way zoom: the control sits on the finding's own
   * badge, so the way back has to be the same click that got there.
   */
  const focusAndFrame = React.useCallback(
    (id: FindingId) => {
      const target = orderedFindings.find((f) => f.id === id)
      if (!target?.bbox) return

      if (preFocusZoom != null && activeId === id) {
        // Back to the whole page, not to whatever zoom preceded the frame: the
        // control's job is "show me this" and "show me the document again", and
        // returning to another magnified view answers neither.
        setZoom(1)
        setPreFocusZoom(null)
        return
      }

      focusFinding(id)
      // Marks that a frame is active; the value is unused on the way out, which
      // always returns to the whole page.
      setPreFocusZoom((prev) => prev ?? zoom)
      setFocusReq((prevReq) => ({ findingId: id, nonce: (prevReq?.nonce ?? 0) + 1 }))
    },
    [orderedFindings, focusFinding, zoom, preFocusZoom, activeId],
  )

  const setZoomManual = React.useCallback((z: number) => {
    setZoom(z)
    setPreFocusZoom(null)
  }, [])

  const step = React.useCallback(
    (delta: -1 | 1) => {
      if (orderedFindings.length === 0) return
      const idx = orderedFindings.findIndex((f) => f.id === activeId)
      const nextIdx =
        idx < 0
          ? delta === 1
            ? 0
            : orderedFindings.length - 1
          : (idx + delta + orderedFindings.length) % orderedFindings.length
      const target = orderedFindings[nextIdx]
      if (target) focusFinding(target.id)
    },
    [orderedFindings, activeId, focusFinding],
  )

  const doVerdict = React.useCallback(
    (findingId: FindingId, verdict: "confirmed" | "rejected" | "needs_sme") => {
      setVerdict.mutate({ reviewId, findingId, verdict })
      const label =
        verdict === "confirmed"
          ? "Confirmed"
          : verdict === "rejected"
            ? "Dismissed"
            : "Sent to a specialist"
      const tone: ToastTone = verdict === "confirmed" ? "success" : "neutral"
      toast({
        tone,
        title: label,
        action: {
          label: "Undo",
          onAction: () => setVerdict.undo({ reviewId, findingId }),
        },
      })
    },
    [reviewId, setVerdict, toast],
  )

  const withActive = React.useCallback(
    (fn: (id: FindingId) => void) => {
      if (activeId) fn(activeId)
    },
    [activeId],
  )

  useReviewHotkeys(
    {
      onNext: () => step(1),
      onPrev: () => step(-1),
      onConfirm: () => withActive((id) => doVerdict(id, "confirmed")),
      onDismiss: () => withActive((id) => doVerdict(id, "rejected")),
      onSendToSme: () => withActive((id) => doVerdict(id, "needs_sme")),
      onMarkCorrected: () => withActive((id) => markCorrected.mutate({ reviewId, findingId: id })),
      onEnterEdit: () =>
        withActive((id) => {
          const f = orderedFindings.find((x) => x.id === id)
          if (f?.authoredBy) setEditingId(id)
        }),
      onCollapse: () => {
        setExpandedId(null)
        setEditingId(null)
      },
      onFocusSearch: () => searchRef.current?.focus(),
      onOpenShortcuts: () => setShortcutsOpen(true),
    },
    { paused: peopleOpen || messagesOpen || addFindingOpen || shortcutsOpen },
  )

  const openComments = React.useCallback((id: FindingId) => {
    setActiveId(id)
    setMessagesOpen(true)
  }, [])

  const submitAddFinding = React.useCallback(
    (input: AddFindingInput) => {
      addFinding.mutate({ reviewId, input })
      toast({ tone: "success", title: `Finding ${input.ruleId} added` })
    },
    [addFinding, reviewId, toast],
  )

  // Autoscroll + expand a newly-created finding so the user never hunts
  // for the record they just added.
  const previousCreated = React.useRef<FindingId | null>(null)
  React.useEffect(() => {
    const created = addFinding.lastCreatedId
    if (created && created !== previousCreated.current) {
      previousCreated.current = created
      focusFinding(created)
    }
  }, [addFinding.lastCreatedId, focusFinding])

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
      <ReviewHeader
        productName={review.productName}
        epaRegNo={review.epaRegNo}
        version={review.version}
        liveState={live.status}
        presence={live.presence}
        view={view}
        onViewChange={setView}
        unreadMessageCount={totalUnread}
        messages={messages}
        findings={review.findings}
        onOpenPeople={() => setPeopleOpen(true)}
        onOpenMessages={() => setMessagesOpen(true)}
        onOpenClientReport={() => {
          void navigate(`/reviews/${reviewId}/output`)
        }}
        backHref="/reviews"
        detectorProfile={review.detectorProfile}
        detectorProfileRequested={review.detectorProfileRequested}
      />

      {globalError && (
        <div className="px-6 pt-3">
          <Alert
            tone="error"
            size="sm"
            title={globalErrorBanner.title || "Your change was not saved"}
            action={
              <Button size="sm" variant="ghost" onClick={dismissGlobalError}>
                Dismiss
              </Button>
            }
          >
            {globalErrorBanner.body}
          </Alert>
        </div>
      )}

      {/* Below a failed save, above the work. A save that failed is about the
          last thing the reviewer did; a blocker is about whether the review ran
          at all, and they can be true at once. */}
      <ReviewBlockerStrip
        review={review}
        role={currentUser?.role}
        onAnswerUnreadPages={(decision) => {
          setAnswerInFlight(decision)
          answerUnreadPages.mutate({ reviewId, decision })
        }}
        answerPending={answerUnreadPages.isPending}
        answerInFlight={answerInFlight}
        answerError={answerUnreadPages.error}
        onRunNow={() => {
          runNow.mutate({ reviewId })
        }}
        runNowPending={runNow.isPending}
        onResubmit={(file) => {
          resubmit.mutate(
            { fpl: file, previousReviewId: Number(reviewId) },
            {
              /*
                 Straight to the replacement: the reviewer's next question is
                 about the file they just filed, not the one they stopped.

                 `replace`, so Back returns to the queue rather than to a
                 stopped review the reviewer has finished with — and pressing
                 Back onto it would offer the upload again, inviting a second
                 version of a label that already has one.
              */
              onSuccess: (created) => {
                navigate(`/reviews/${String(created.id)}`, { replace: true })
              },
            },
          )
        }}
        resubmitPending={resubmit.isPending}
        resubmitError={resubmit.error}
      />

      <div className="relative min-h-0 min-w-0 flex-1">
        {view === "findings" && (
          <SplitPane
            storageKey="review:findings"
            defaultFraction={0.62}
            minFraction={0.4}
            maxFraction={0.78}
            ariaLabel="Resize PDF and rail"
            // `isolate` keeps the floating controls' z-index local to this pane.
            // Without it they compete with the whole application and paint over
            // a modal that is meant to be covering the workspace.
            left={
              <div className="relative isolate h-full min-h-0">
                <PdfSurface
                  documentUrl={review.fplUrl}
                  label={review.fplName}
                  subtitle="Final Printed Label"
                  provenance={`Reg. ${review.epaRegNo} · ${review.version}`}
                  {...(reReadAllowed
                    ? {
                        onReReadPage: (p: number) => {
                          forceOcr.mutate({ reviewId, side: "fpl", page: p })
                        },
                        reReadPending: forceOcr.isPending,
                      }
                    : {})}
                  awaitingFindings={isRunning(review.status)}
                  page={page}
                  onPageCount={setFplPages}
                  zoom={zoom}
                  onZoomChange={setZoom}
                  onManualZoom={setZoomManual}
                  focusRequest={focusReq}
                  findings={review.findings}
                  activeFindingId={activeId}
                  onSelectFinding={focusFinding}
                  showBoxes={showBoxes}
                  cursors={live.cursors}
                  drawMode={drawMode}
                  sections={PAGE_SECTIONS}
                  onDrawComplete={(r) => {
                    setDrawMode(false)
                    setDrawContext({
                      bbox: r.bbox,
                      // Omitted rather than set to undefined: the section is
                      // absent when the box was drawn outside every one.
                      ...(r.section
                        ? { sectionKey: r.section.key, sectionLabel: r.section.label }
                        : {}),
                    })
                    setAddFindingOpen(true)
                  }}
                  onDrawCancel={() => setDrawMode(false)}
                />
                {drawMode && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-20 z-30 flex justify-center px-4">
                    <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-border bg-surface/95 px-3 py-1.5 text-text-2 shadow-e3 backdrop-blur">
                      <span className="grid size-5 place-items-center rounded-full bg-brand text-white font-id text-[10px] font-semibold">
                        +
                      </span>
                      <span className="text-caption">
                        Drag a box on the printed label to add a finding
                      </span>
                      <span className="text-text-3">·</span>
                      <kbd className="rounded-sm border border-border px-1 py-0.5 font-id text-[10px] text-text-2">
                        Esc
                      </kbd>
                      <span className="text-caption text-text-3">to cancel</span>
                    </div>
                  </div>
                )}
                <FindingRibbon
                  findings={orderedFindings}
                  pageCount={fplPages}
                  page={page}
                  onSelectFinding={focusFinding}
                />
                {/* Top, not bottom: this is the control a reviewer reaches for
                    while reading, and the eye is already at the top of the page
                    where the document's own header sits. */}
                <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center px-4">
                  <PdfControls
                    findings={orderedFindings}
                    activeFindingId={activeId}
                    onSelectFinding={focusFinding}
                    page={page}
                    pageCount={review.pageCount}
                    onPageChange={setPage}
                    showBoxes={showBoxes}
                    onToggleBoxes={() => {
                      setShowBoxes((v) => !v)
                    }}
                    onZoomToActive={() => {
                      if (activeId) focusAndFrame(activeId)
                    }}
                    isFramed={preFocusZoom != null}
                  />
                </div>
                <div className="pointer-events-none absolute bottom-4 right-4 z-10 flex justify-end">
                  <PdfZoomControl zoom={zoom} onZoomChange={setZoomManual} />
                </div>
              </div>
            }
            right={
              <FindingsRail
                governingMaster={review.governingMaster}
                findings={orderedFindings}
                loading={false}
                running={isRunning(review.status)}
                failed={review.status === "failed"}
                hasDocument={review.fplUrl !== ""}
                stage={review.pipelineStage}
                activeFindingId={activeId}
                expandedFindingId={expandedId}
                editingFindingId={editingId}
                canDispose
                onSelect={focusFinding}
                onFrame={focusAndFrame}
                framedFindingId={preFocusZoom != null ? activeId : null}
                onToggleExpand={(id) => setExpandedId((cur) => (cur === id ? null : id))}
                onEnterEdit={(id) => setEditingId(id)}
                onCancelEdit={() => setEditingId(null)}
                onSave={(findingId, patch) => {
                  editNarrative.mutate({ reviewId, findingId, patch })
                  setEditingId(null)
                  toast({ tone: "success", title: "Finding updated" })
                }}
                onConfirm={(id) => doVerdict(id, "confirmed")}
                onDismiss={(id) => doVerdict(id, "rejected")}
                onSendToSme={(id) => doVerdict(id, "needs_sme")}
                onMarkCorrected={(findingId) => markCorrected.mutate({ reviewId, findingId })}
                onOpenComments={openComments}
                onAddFinding={() => {
                  setDrawContext(null)
                  setDrawMode(true)
                }}
                searchRef={searchRef}
              />
            }
          />
        )}

        {view === "side_by_side" && (
          <SideBySide
            fplUrl={review.fplUrl}
            fplLabel={review.fplName}
            fplProvenance={`Reg. ${review.epaRegNo} · ${review.version}`}
            fplPageCount={review.pageCount}
            compareOptions={compareOptions}
            compareId={compareId}
            onCompareChange={setCompareId}
            page={page}
            onPageChange={setPage}
            zoom={zoom}
            onZoomChange={setZoomManual}
            findings={review.findings}
            activeFindingId={activeId}
            onSelectFinding={focusFinding}
          />
        )}
      </div>

      <PeopleRoutingSheet
        open={peopleOpen}
        onOpenChange={setPeopleOpen}
        actor={currentUser ? { id: currentUser.id, role: currentUser.role } : null}
        review={{
          ...review,
          assignment: {
            ...review.assignment,
            assignee: people?.assignee
              ? {
                  userId: people.assignee.userId,
                  name: people.assignee.name,
                  role: people.assignee.role,
                  sinceISO: review.updatedAt,
                }
              : null,
            events: [...events],
          },
        }}
        colleagues={[...colleagues]}
        onAssign={async (assigneeId, name, role, note) => {
          try {
            await assignReview.mutateAsync({
              reviewId,
              assigneeId,
              assigneeName: name,
              assigneeRole: role,
              note,
            })
            toast({ tone: "success", title: `Handed to ${name}` })
          } catch (err) {
            toast({
              tone: "error",
              title: "Handoff failed",
              body: bannerFor(err, "the handoff", "save").body,
            })
          }
        }}
        onReturnToPool={async () => {
          try {
            await returnToPool.mutateAsync({ reviewId })
            toast({ tone: "neutral", title: "Returned to the pool" })
          } catch (err) {
            toast({
              tone: "error",
              title: "Return failed",
              body: bannerFor(err, "the return", "save").body,
            })
          }
        }}
        onAdvance={async (secondaryReviewerId) => {
          try {
            await advanceStage.mutateAsync({ reviewId, secondaryReviewerId })
            toast({ tone: "success", title: "Advanced" })
          } catch (err) {
            toast({
              tone: "error",
              title: "Advance failed",
              body: bannerFor(err, "this review", "save").body,
            })
          }
        }}
        onSkipSecondPass={async () => {
          try {
            await skipSecondPass.mutateAsync({ reviewId })
            toast({ tone: "neutral", title: "Second pass skipped" })
          } catch (err) {
            toast({
              tone: "error",
              title: "Skip failed",
              body: bannerFor(err, "the skip", "save").body,
            })
          }
        }}
        onUnarchive={async () => {
          try {
            await unarchiveReview.mutateAsync({ reviewId })
            toast({ tone: "neutral", title: "Back in the queue" })
          } catch (err) {
            toast({
              tone: "error",
              title: "Unarchive failed",
              body: bannerFor(err, "the unarchive", "save").body,
            })
          }
        }}
        onArchive={async () => {
          try {
            await archiveReview.mutateAsync({ reviewId })
            toast({ tone: "neutral", title: "Archived" })
          } catch (err) {
            toast({
              tone: "error",
              title: "Archive failed",
              body: bannerFor(err, "the archive", "save").body,
            })
          }
        }}
      />

      <MessagesSheet
        open={messagesOpen}
        onOpenChange={setMessagesOpen}
        messages={messages}
        findings={orderedFindings}
        activeFindingId={activeId}
        clientAttached={Boolean(review.client)}
        archived={review.assignment.archived}
        onFocusFinding={focusFinding}
        // A viewer reads the conversation but does not take part in it: no
        // composer, no typing line of theirs, and nothing marked read.
        onSend={
          canConverse(currentUser?.role)
            ? ({ body, findingId }) => {
                postMessage.mutate({ reviewId, body, findingId })
              }
            : undefined
        }
        typing={live.typing}
        {...(canConverse(currentUser?.role) ? { onTyping: signalTyping } : {})}
        onRetry={(message) => {
          // The failed row carries the only copy of what was typed, so the text
          // is read back off it rather than kept in a second piece of state.
          postMessage.mutate({
            reviewId,
            body: message.body,
            findingId: message.findingId,
            replaces: message.id,
          })
        }}
        onMarkRead={
          canConverse(currentUser?.role)
            ? (findingId) => markMessagesRead.mutate({ reviewId, findingId })
            : undefined
        }
      />

      <AddFindingDialog
        open={addFindingOpen}
        onOpenChange={(o) => {
          setAddFindingOpen(o)
          if (!o) setDrawContext(null)
        }}
        onSubmit={submitAddFinding}
        rules={RULE_CATALOG}
        drawContext={drawContext}
      />

      <ReviewShortcuts open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  )
}
